import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

function Quote({ text, context }: { text: string; context: string }) {
  return (
    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(140,82,255,0.2)', borderLeft: '3px solid #8c52ff', borderRadius: 2, mb: 3 }}>
      <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#d0d0e0', lineHeight: 1.7, fontStyle: 'italic', mb: 0.75 }}>
        "{text}"
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.6)' }}>
        {context}
      </Typography>
    </Box>
  );
}

export default function BlogSunyCortlandPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="SUNY Cortland Daycare — How Audio Detection Would Have Changed Everything" description="A case study in verbal abuse at a campus daycare facility and how audio AI trained on childcare behavior would have flagged it weeks earlier." path="/blog/suny-cortland-audio-detection" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Audio Detection</Typography>
        </Box>

        {/* Tag badge */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(140,82,255,0.08)', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#8c52ff', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Audio Detection · 7 min read</Typography>
        </Box>

        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          "No, Don't. It Hurts." — What Audio Would Have Caught at the SUNY Cortland Daycare.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          Four workers — including the executive director — were arrested for abusing children at a university-run daycare in 2026. The cameras captured it. The audio captured it too. But no system was listening. Here's what audio AI detection would have flagged from the very first week.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Section title="What the recordings captured">
          <Para>
            The surveillance system at SUNY Cortland Child Care Center did its job. It captured everything. And what it captured — in sound as much as in image — tells the story of why audio detection is not optional.
          </Para>

          <Quote
            text="No, stop."
            context="A child, repeatedly, March 27, 2026 — as a worker aggressively yelled at them. Captured on surveillance video."
          />

          <Quote
            text="No, don't. It hurts."
            context="A child crying off-camera, March 31, 2026. Captured on surveillance audio."
          />

          <Para>
            Those words belong to children who were two, three, four years old. They were in a daycare run by a university. They were in the care of professionals — including an executive director with decades in childcare. And they were saying "no" and "it hurts" into a room where the recordings were running, and no system was analyzing what it heard.
          </Para>
          <Para>
            The incidents that would eventually lead to the arrest of four staff members — including Executive Director Karen Diescher, 50 — stretched from January through April 2026. They involved yelling, physical restraint, pushing, and something particularly disturbing: a worker who ordered other children to kick and step on a child who wouldn't move.
          </Para>
        </Section>

        <Section title="The case: SUNY Cortland Child Care Center, 2026">
          <Para>
            The SUNY Cortland Child Care Center is a university-operated facility in Cortland, New York. It is the kind of facility that should, by every reasonable standard, be among the safest in the country — staffed by trained professionals, subject to institutional oversight, operating on a university campus.
          </Para>
          <Para>
            Between January and April 2026, court documents allege that multiple workers engaged in a sustained pattern of physical and emotional abuse against children in their care. Four employees were arrested and charged with endangering the welfare of a child:
          </Para>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              {
                name: 'Aimee Wyatt, 50',
                charges: '4 counts endangering welfare of a child + 1 count criminal solicitation',
                detail: 'Instructed children to kick and step on another child (April 7); trapped a 3-year-old in a bathroom and pushed them to the floor while holding the door shut (Feb 16); pushed a child with her foot (Jan 21). Her own voluntary statement: "I told the children they could step on another child that refused to move. I said it in a harsh tone."',
              },
              {
                name: 'Karen Diescher, 50 — Executive Director',
                charges: '3 counts endangering welfare of a child',
                detail: 'Charged as director over incidents that occurred under her supervision, including after being notified of at least one incident.',
              },
              {
                name: 'Kelsi Carlisle, 29',
                charges: '2 counts endangering welfare of a child',
                detail: 'Pushed a child in the chest and yelled at them at face level (April 10); restrained a child on their sleep mat with her body (April 8). Her own statement: "I acknowledge that I pushed a child in the chest, and I yelled at them at face level."',
              },
              {
                name: 'Heather Hurteau, 57',
                charges: '2 counts endangering welfare of a child',
                detail: 'Kept a child in a bathroom for up to 20 minutes to force toilet use; used a "sharp, harsh tone daily" with children. Witnessed the February 16 bathroom incident involving Wyatt and did not intervene.',
              },
            ].map(({ name, charges, detail }) => (
              <Box key={name} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{name}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#8c52ff', fontWeight: 600, mb: 1 }}>{charges}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="The audio that no one was monitoring">
          <Para>
            This case is not primarily a story about physical abuse — though the physical abuse was real and documented. It is a story about a vocal environment that was hostile to children every single day.
          </Para>
          <Para>
            Court records describe Heather Hurteau using a "sharp, harsh tone daily" with children. Kelsi Carlisle yelled at children at face level. Aimee Wyatt used what she herself described as a "harsh tone" when directing children to hurt one another. A video from March 27 captured a worker "aggressively yelling" at a child who kept repeating "no, stop."
          </Para>
          <Para>
            Every one of these audio signatures is detectable. Not with a human listening to hours of recording, but with AI trained to identify behavioral anomalies in real time — raised voice frequency and intensity, vocal patterns directed at specific children, deviation from a worker's own communication baseline, and most critically, distress vocalizations from children: "no, stop," "no, don't, it hurts."
          </Para>

          <Box sx={{ p: 3, bgcolor: 'rgba(140,82,255,0.04)', border: '1px solid rgba(140,82,255,0.15)', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#8c52ff', mb: 1.5 }}>What SMCLA's audio AI would have flagged:</Typography>
            <Stack spacing={1}>
              {[
                'Aggressive yelling directed at a child — detected as a volume and tone anomaly against both the facility baseline and that worker\'s own pattern',
                'A child\'s repeated "no, stop" — distress vocalization pattern, flagged within seconds',
                'A child crying "no, don\'t, it hurts" — acute distress event, immediate alert',
                'Hurteau\'s daily harsh tone — cumulative behavioral drift, detected within days as a pattern deviation, not just a single incident',
                'A worker issuing commands in a harsh tone followed by sounds of physical activity and child distress — combined audio + visual signature',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#8c52ff', mt: 0.7, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Para>
            The March 27 incident alone — a worker aggressively yelling while a child says "no, stop" — is an unambiguous audio signature. Not ambiguous. Not borderline. It is the exact behavioral pattern that audio AI is designed to surface.
          </Para>
        </Section>

        <Section title="How it was discovered — and the failure that followed">
          <Para>
            The April 7 incident — Aimee Wyatt ordering children to kick and step on a child who wouldn't move, lasting three minutes — was witnessed by interns at the facility.
          </Para>
          <Para>
            The interns reported it on April 14. The executive director was informed on April 15. Child Protective Services and the Office of Children and Family Services were contacted. And the facility's planned response to Wyatt — the worker who had just orchestrated child-on-child assault — was, according to court records, "a conversation and possibly a verbal warning."
          </Para>

          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.15)', borderLeft: '3px solid #ff6b6b', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>
              The institutional response to documented child abuse: a verbal warning.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
              CPS ultimately escorted Wyatt from the building and placed her on administrative leave. It took external intervention — not internal action — to remove a worker who had told children to assault each other. The executive director herself now faces criminal charges.
            </Typography>
          </Box>

          <Para>
            This is the second failure in this case. The first failure was the absence of any system that would have flagged the hostile vocal environment in January, February, March — before the April 7 incident that interns witnessed. The second failure was an institutional chain of command that, when informed, planned to give a verbal warning.
          </Para>
          <Para>
            SMCLA alerts go directly to the facility owner — not through the management chain that failed here. An owner who receives a 60-second clip of a worker aggressively yelling at a child saying "no, stop" does not need to decide whether to issue a verbal warning. They call the police.
          </Para>
        </Section>

        <Section title="The scenario that should have happened — in January">
          <Para>
            The earliest documented incident in this case was January 21. But the audio record suggests the hostile environment predates even that. Hurteau's "daily harsh tone" does not begin on a specific date — it is a pattern that builds.
          </Para>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              {
                time: 'Week 1 of deployment',
                event: 'Baseline established',
                detail: "SMCLA maps each worker's normal vocal patterns during caregiving — tone, volume, frequency, word patterns directed at children. This becomes the individual baseline against which deviations are measured.",
              },
              {
                time: 'First week of January',
                event: "Hurteau's daily harsh tone flagged",
                detail: "Within days, cumulative pattern analysis identifies that one worker's tone toward children deviates consistently from both the facility baseline and her own baseline. An alert is sent to the owner with representative clips.",
              },
              {
                time: 'January 21',
                event: 'Physical incident detected — combined signal',
                detail: "Audio captures a sharp command followed by a child's sudden distress sound. Video shows physical contact. The combined signal produces an immediate alert.",
              },
              {
                time: 'February 16',
                event: 'Bathroom incident — audio signature',
                detail: "A child's distress vocalizations in an enclosed space, combined with the absence of normal caregiving audio, flags an anomaly. Alert fires.",
              },
              {
                time: 'March 27',
                event: '"No, stop" — acute distress flag',
                detail: "A worker's aggressive vocal pattern combined with a child's repeated distress phrase triggers an immediate high-priority alert. The owner receives the clip within 60 seconds.",
              },
              {
                time: 'March 27 + 61 seconds',
                event: 'Owner acts',
                detail: 'The clip is unambiguous. The worker is removed before March 31. Before "no, don\'t, it hurts." Before April 7. Before four people are charged with criminal offenses.',
              },
            ].map(({ time, event, detail }) => (
              <Box key={time} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#8c52ff', fontWeight: 600 }}>{time}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#f0f0f8', fontWeight: 700 }}>{event}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="What a camera sees. What audio hears.">
          <Para>
            Every major abuse case of the past five years has had cameras. The footage almost always exists. But footage that sits in storage until someone reports something is not safety infrastructure — it is an evidence archive. It documents what happened. It does not change what happened.
          </Para>
          <Para>
            The SUNY Cortland case is distinct because the verbal evidence is the most damning part. A worker who says "I told the children they could step on another child" in a "harsh tone" has revealed something about her relationship to the children in her care that no camera image alone captures. The words are the abuse. The tone is the culture.
          </Para>
          <Para>
            A child who says "no, don't, it hurts" off-camera is invisible to visual-only monitoring. They are completely visible — completely audible — to audio AI. This is the category gap that SMCLA closes: the harm that leaves no mark in any image but is documented in sound, every day, in real time.
          </Para>

          <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#5ce1e6', mb: 1 }}>
              "No, don't. It hurts." was captured on audio.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>
              The recording was already running. The question is whether anyone — or anything — was listening.
            </Typography>
          </Box>
        </Section>

        <Section title="The lasting harm: what parents reported">
          <Para>
            By the time investigators became involved, the damage had already shaped how children experienced the world.
          </Para>
          <Para>
            One grandmother told investigators that her three-year-old grandson — who had previously been comfortable with bathrooms — now cries and screams in bathrooms, freezes when he sees other children cry near adults, and fears using bathrooms at all. These are not temporary reactions. The behaviors that form in early childhood under sustained stress become the baseline from which children develop. A three-year-old who is told bathrooms are places of punishment does not simply unlearn that.
          </Para>
          <Para>
            Other parents described children returning home with bruises. Children who had begun saying "don't hurt me" and "don't touch me like that" — phrases no child that age should need. Nightmares. Refusal to return. Regressive behavior that rolled back months of development.
          </Para>
          <Para>
            These are the consequences of a monitoring gap — not a gap in cameras, but a gap in what was being done with what the cameras and microphones already heard.
          </Para>
        </Section>

        {/* Source attribution */}
        <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', mb: 1.5 }}>Sources</Typography>
          <Stack spacing={1}>
            {[
              { label: 'CNY Central — workers admit to abusing kids in court documents', url: 'https://cnycentral.com/news/local/suny-cortland-childcare-workers-admit-to-abusing-kids-in-court-documents' },
              { label: 'CNY Central — deeper dive into court documents of 4 SUNY Cortland arrests', url: 'https://cnycentral.com/news/local/digging-deeper-into-court-documents-of-arrests-of-4-suny-cortland-childcare-staff-members' },
              { label: 'CNY Central — executive director and 3 employees arrested', url: 'https://cnycentral.com/news/local/executive-director-3-employees-arrested-in-suny-cortland-child-endangerment-case' },
              { label: 'LocalSYR — court records shed new light on SUNY Cortland daycare misconduct', url: 'https://www.localsyr.com/news/local-news/court-records-daycare-cortland/' },
              { label: 'Finger Lakes Daily News — abuse allegations surface at SUNY Cortland daycare', url: 'https://www.fingerlakesdailynews.com/local/abuse-allegations-surface-at-suny-cortland-daycare/' },
              { label: 'Syracuse.com — police report on SUNY Cortland daycare arrests', url: 'https://www.syracuse.com/crime/2026/05/police-suny-cortland-daycare-workers-hurt-children-ordered-kids-to-beat-child-no-dont-it-hurts.html' },
            ].map(({ label, url }) => (
              <Box key={url} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(148,153,176,0.4)', mt: 0.7, flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5, textDecoration: 'none', '&:hover': { color: '#5ce1e6', textDecoration: 'underline' } }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            Hear what your cameras can't see
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            "No, don't. It hurts." was already on the recording. SMCLA is the system that listens — in real time, across every room, every shift — and alerts you before the harm compounds.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#8c52ff', color: '#ffffff', borderRadius: 2, '&:hover': { bgcolor: '#7a45e0' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
